import { Injectable } from '@angular/core';
import { Vector3, Camera, Object3D } from 'three';
import { BehaviorSubject } from 'rxjs';
import {Server} from "../../../../../services/model/server.model";

interface PopupPosition {
	x: number;
	y: number;
	isVisible: boolean;
}

@Injectable({
	providedIn: 'root'
})
export class PopupService {
	// Position du popup
	private popupPositionSubject = new BehaviorSubject<PopupPosition>({
		x: 0,
		y: 0,
		isVisible: false
	});
	public popupPosition$ = this.popupPositionSubject.asObservable();

	// Liste des serveurs
	private serversListSubject = new BehaviorSubject<Server[]>([]);
	public serversList$ = this.serversListSubject.asObservable();

	// Référence à l'élément canvas et à la caméra
	private canvas: HTMLElement | null = null;
	private camera: Camera | null = null;

	// Objet 3D du bouton +X
	private plusButtonObject: Object3D | null = null;

	constructor() {}

	// Initialisation du service avec les références nécessaires
	initialize(canvas: HTMLElement, camera: Camera) {
		this.canvas = canvas;
		this.camera = camera;
	}

	// Définir l'objet 3D correspondant au bouton +X
	setPlusButtonObject(button: Object3D) {
		this.plusButtonObject = button;
	}

	// Afficher les serveurs supplémentaires et positionner le popup
	showExtraServers(servers: Server[]) {
		// Mettre à jour la liste de serveurs
		this.serversListSubject.next(servers);

		// Si aucun serveur, cacher le popup
		if (servers.length === 0) {
			this.hideExtraServers();
			return;
		}

		// Si le bouton n'existe pas ou si canvas/camera ne sont pas définis, centrer le popup
		if (!this.plusButtonObject || !this.canvas || !this.camera) {

			// Calculer le centre de l'écran
			const rect = this.canvas ? this.canvas.getBoundingClientRect() : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
			const centerX = rect.left + rect.width / 2 + window.scrollX;
			const centerY = rect.top + rect.height / 2 + window.scrollY;

			this.popupPositionSubject.next({
				x: centerX,
				y: centerY,
				isVisible: true
			});
			return;
		}

		try {
			// Position 3D du bouton
			const position = new Vector3();
			this.plusButtonObject.getWorldPosition(position);

			// Calculer les coordonnées d'écran pour cette position 3D
			const screenPosition = this.getScreenPosition(position);

			if (screenPosition) {

				this.popupPositionSubject.next({
					x: screenPosition.x,
					y: screenPosition.y + 65,
					isVisible: true
				});
			} else {

				const rect = this.canvas.getBoundingClientRect();
				const centerX = rect.left + rect.width / 2 + window.scrollX;
				const centerY = rect.top + rect.height / 2 + window.scrollY;

				this.popupPositionSubject.next({
					x: centerX,
					y: centerY,
					isVisible: true
				});
			}
		} catch (error) {
			// En cas d'erreur, positionner au centre
			const rect = this.canvas ? this.canvas.getBoundingClientRect() : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
			const centerX = rect.left + rect.width / 2 + window.scrollX;
			const centerY = rect.top + rect.height / 2 + window.scrollY;

			this.popupPositionSubject.next({
				x: centerX,
				y: centerY,
				isVisible: true
			});
		}

		this.updatePositionFromWorldCoords();

	}

	updatePositionFromWorldCoords(worldPosition?: Vector3) {
		if (!this.canvas || !this.camera) {
			return;
		}

		try {
			// Si on ne fournit pas de position, utiliser celle du bouton
			const position = worldPosition || new Vector3();
			if (!worldPosition && this.plusButtonObject) {
				this.plusButtonObject.getWorldPosition(position);
			}

			// Calculer les coordonnées d'écran pour cette position 3D
			const screenPosition = this.getScreenPosition(position);

			if (screenPosition) {
				// Mettre à jour la position du popup sans changer son état de visibilité
				const currentVisibility = this.popupPositionSubject.value.isVisible;

				this.popupPositionSubject.next({
					x: screenPosition.x,
					y: screenPosition.y + 65, // Offset vertical
					isVisible: currentVisibility // Conserver l'état de visibilité actuel
				});
			}
		} catch (error) {
			console.error('Error updating position:', error);
		}
	}

	// Méthode pour convertir une position 3D en position d'écran 2D
	getScreenPosition(worldPosition: Vector3): { x: number, y: number } | null {
		if (!this.canvas || !this.camera) {
			console.warn('Canvas or camera not initialized');
			return null;
		}

		// Créer une copie pour ne pas modifier l'original
		const positionCopy = worldPosition.clone();

		// Obtenir les dimensions et la position du canvas par rapport à la page
		const rect = this.canvas.getBoundingClientRect();

		// Projeter le point 3D dans l'espace normalisé du dispositif (-1 à +1)
		positionCopy.project(this.camera);

		// Convertir les coordonnées normalisées en pixels relative au canvas
		const x = ((positionCopy.x + 1) / 2) * rect.width + rect.left;
		const y = ((-positionCopy.y + 1) / 2) * rect.height + rect.top;

		// Ajouter le défilement de la page si nécessaire
		const pageX = x + window.scrollX;
		const pageY = y + window.scrollY;

		// Vérifier si la position est dans le champ de vision (avec une marge)
		const margin = 100; // pixels de marge
		if (isNaN(pageX) || isNaN(pageY) ||
			pageX < rect.left - margin || pageX > rect.right + margin ||
			pageY < rect.top - margin || pageY > rect.bottom + margin) {
			return null;
		}

		return { x: pageX, y: pageY };
	}

	// Fermer le popup
	hideExtraServers() {
		this.popupPositionSubject.next({
			...this.popupPositionSubject.value,
			isVisible: false
		});
	}
}
